import { Plus, User, DollarSign, LogOut, Users, X, Move, ArrowLeftRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import clsx from 'clsx';

const PlayerSlot = ({ children, isExtraSlot, isMoving, onMoveCancel, movingFromIndex }) => {
    return (
        <div 
            className={clsx(
                "flex items-center justify-between bg-slate-800/80 rounded border overflow-hidden h-[26px] transition-all px-1 py-0 shadow-sm relative group w-full",
                isExtraSlot ? "opacity-20 pointer-events-none border-white/5" : "border-white/5 hover:border-amber-500/30",
                isMoving && "border-amber-500 shadow-[0_0_15px_rgba(245,158,11,0.2)] bg-amber-500/10",
                movingFromIndex && !isMoving && !isExtraSlot && "border-dashed border-amber-500/40 animate-pulse"
            )}
        >
            {children}
            {isMoving && (
                <div className="absolute inset-0 bg-amber-500/10 flex items-center justify-center pointer-events-none">
                    <div className="bg-amber-500 text-slate-900 text-[8px] font-black px-1.5 py-0.5 rounded shadow-lg uppercase tracking-tighter">
                        Moving...
                    </div>
                </div>
            )}
        </div>
    );
};

export const PlayerList = ({
    players = [],
    onAddPlayer,
    onRemovePlayer,
    onAddBuyin,
    onDeleteBuyin,
    onCashout,
    onCashoutAll,
    buyins = [],
    cashouts = [],
    maxSlots = 11,
    cashedOutPlayers = [],
    readOnly = false,
    onPlayerClick,
    movingFromIndex = null,
    onMoveStart,
    onMoveCancel,
    moveOnly = false
}) => {
    const { t } = useLanguage();

    const slots = Array.from({ length: 11 }, (_, i) => players[i] || null);

    const formatCurrency = (amount, sign = "") => {
        const absAmount = Math.abs(amount ?? 0).toFixed(0).toLocaleString();
        if (sign === "+") return `+ ₪ ${absAmount}`;
        if (sign === "-") return `- ₪ ${absAmount}`;
        return `₪ ${absAmount}`;
    };

    const formatTime = (timestamp) => {
        const date = new Date(timestamp);
        return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
    };

    const getSlotBuyins = (slotIndex) => buyins.filter(b => b.slotIndex === slotIndex + 1);
    const getTotalBuyins = (slotIndex) => getSlotBuyins(slotIndex).reduce((s, b) => s + b.amount, 0);

    return (
        <div className="flex flex-col h-full bg-slate-900/20 rounded-lg overflow-hidden p-0.5 gap-0.5 overflow-y-auto no-scrollbar relative z-10" dir="rtl">
            
            <div className="flex flex-col gap-0.5 w-full">
                {slots.map((player, index) => {
                    const isExtraSlot = index >= maxSlots;
                    const slotBuyins = getSlotBuyins(index);
                    const totalIn = getTotalBuyins(index);
                    const isMoving = movingFromIndex === (index + 1);

                    const cashTotal = slotBuyins.filter(b => b.method === 'cash').reduce((s, b) => s + b.amount, 0);
                    const creditTotal = slotBuyins.filter(b => b.method === 'credit').reduce((s, b) => s + b.amount, 0);
                    
                    const isMixed = cashTotal > 0 && creditTotal > 0;
                    const totalColor = isMixed ? 'text-yellow-400' : (cashTotal > 0 ? 'text-emerald-400' : 'text-amber-400');
                    const totalBorder = isMixed ? 'border-yellow-500/20' : (cashTotal > 0 ? 'border-emerald-500/20' : 'border-amber-500/20');
                    const totalBg = isMixed ? 'bg-yellow-500/5' : (cashTotal > 0 ? 'bg-emerald-500/5' : 'bg-amber-500/5');

                    return (
                        <PlayerSlot 
                            key={`seat-${index + 1}`} 
                            isExtraSlot={isExtraSlot} 
                            isMoving={isMoving}
                            onMoveCancel={onMoveCancel}
                            movingFromIndex={movingFromIndex}
                        >
                            
                            <div 
                                onClick={() => {
                                    if (movingFromIndex && movingFromIndex !== (index+1)) {
                                        onAddPlayer?.(index + 1); // executeMove logic
                                    } else {
                                        player && onPlayerClick?.(index + 1, player, slotBuyins);
                                    }
                                }}
                                className={clsx(
                                    "flex items-center flex-1 h-full min-w-0 transition-opacity px-1",
                                    player ? "cursor-pointer hover:opacity-80 active:opacity-60" : "cursor-pointer"
                                )}
                            >
                                <div className="flex items-center gap-1 basis-[22%] shrink-0 min-w-0 h-full" dir="rtl">
                                    <div className={clsx(
                                        "w-3.5 h-3.5 rounded flex items-center justify-center shrink-0 border shadow-inner",
                                        isMoving ? "bg-amber-500 border-amber-400" : "bg-slate-900/50 border-white/10"
                                    )}>
                                        <span className={clsx("text-[7.5px] font-bold tabular-nums", isMoving ? "text-slate-900" : "text-slate-400")}>
                                            {index + 1}
                                        </span>
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        {player ? (
                                            <span className="font-bold text-slate-100 text-[9.5px] truncate block pr-0.5 leading-tight">{player.name}</span>
                                        ) : (
                                            <span className="text-slate-500/50 text-[9px] font-light italic truncate block pr-0.5 leading-tight tracking-widest">
                                                {movingFromIndex ? 'העבר לכאן' : 'פנוי'}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="basis-[50%] grow min-w-0 h-full flex items-center justify-end px-1 overflow-hidden" dir="ltr">
                                    {player && slotBuyins.length >= 1 && (
                                        <div className="flex items-center gap-0.5 overflow-x-auto no-scrollbar py-0.5">
                                            {[...slotBuyins].sort((a,b) => new Date(a.timestamp || 0) - new Date(b.timestamp || 0)).map((buyin, i) => (
                                                <div key={buyin.id || `buyin-${i}`} className="flex flex-col items-center justify-center bg-slate-900/60 rounded px-1 py-0.5 border border-white/5 shrink-0 h-4.5 min-w-[1.8rem]">
                                                    <span className={clsx(
                                                        "text-[7px] font-black tabular-nums leading-none",
                                                        buyin.method === 'credit' ? 'text-amber-400' : 'text-emerald-400'
                                                    )}>
                                                        {buyin.amount ? buyin.amount.toLocaleString() : "0"}
                                                    </span>
                                                    {buyin.timestamp && (
                                                        <span className="text-[5px] text-slate-500 font-bold tabular-nums leading-none mt-0.5 opacity-80">
                                                            {formatTime(buyin.timestamp)}
                                                        </span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div className="basis-[16%] shrink-0 flex items-center justify-center h-full px-1" dir="ltr">
                                    {player && totalIn > 0 && (
                                        <div className="flex items-center gap-0.5 w-full justify-center">
                                            {slotBuyins.length >= 2 && (
                                                <span className="text-[8px] text-slate-600 font-bold -ml-0.5 shrink-0">=</span>
                                            )}
                                            <div className={clsx(
                                                "flex flex-col items-center rounded px-2 py-0.5 border w-fit min-w-[2.5rem] h-5.5 justify-center shadow-inner shrink-0",
                                                slotBuyins.length >= 2 ? (totalBg + " " + totalBorder) : "bg-slate-900/40 border-white/5"
                                            )}>
                                                <span className={clsx(
                                                    "text-[9px] font-black tabular-nums leading-none",
                                                    slotBuyins.length >= 2 ? totalColor : (slotBuyins[0].method === 'credit' ? 'text-amber-400' : 'text-emerald-400')
                                                )}>
                                                    {formatCurrency(totalIn)}
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="basis-[12%] shrink-0 flex items-center justify-end h-full gap-0.5 pr-0.5">
                                {!player && !readOnly ? (
                                    <button 
                                        onClick={() => onAddPlayer?.(index + 1)} 
                                        className={clsx(
                                            "h-5 w-6 flex items-center justify-center rounded transition-colors border shadow-sm",
                                            movingFromIndex ? "text-amber-400 bg-amber-500/20 border-amber-500/40" : "text-amber-400 bg-amber-500/10 hover:bg-amber-500/20 border-amber-500/20"
                                        )}
                                    >
                                        <Plus className="w-2.5 h-2.5" />
                                    </button>
                                ) : player && !readOnly && (
                                    <div className="flex gap-1">
                                        <button 
                                            onClick={(e) => { 
                                                e.stopPropagation(); 
                                                if (isMoving) onMoveCancel?.();
                                                else onMoveStart?.(index + 1); 
                                            }} 
                                            className={clsx(
                                                "h-6 w-6 flex items-center justify-center rounded transition-all border shadow-sm active:scale-90",
                                                isMoving ? "bg-amber-500 text-slate-950 border-amber-400" : "bg-slate-800 hover:bg-slate-700 border-white/10 text-amber-500"
                                            )}
                                            title="הזז שחקן"
                                        >
                                            <ArrowLeftRight className="w-3 h-3" />
                                        </button>
                                        {!moveOnly && (
                                            <>
                                                <button onClick={(e) => { e.stopPropagation(); onAddBuyin?.(index + 1); }} className="h-6 w-6 flex items-center justify-center bg-emerald-500/10 hover:bg-emerald-500/20 active:bg-emerald-500/30 text-emerald-400 rounded transition-all border border-emerald-500/20 active:scale-90" title="הוסף קנייה">
                                                    <Plus className="w-3 h-3" />
                                                </button>
                                                <button onClick={(e) => { e.stopPropagation(); onCashout?.(index + 1); }} className="h-6 w-6 flex items-center justify-center bg-rose-500/10 hover:bg-rose-500/20 active:bg-rose-500/30 text-rose-500 rounded transition-all border border-rose-500/20 active:scale-90" title="יציאה">
                                                    <LogOut className="w-3 h-3 text-rose-500" />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        </PlayerSlot>
                    );
                })}
            </div>

            {cashedOutPlayers.length > 0 && (
                <div className="mt-2 border-t border-white/5 pt-2">
                    <div className="px-2 mb-1">
                        <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">היסטוריית שחקנים</span>
                    </div>
                    <div className="flex flex-col gap-1 opacity-60">
                        {cashedOutPlayers.map(cp => {
                            const net = cp.totalCashout - cp.totalBuyIn;
                            return (
                                <div key={cp.playerId} className="flex items-center justify-between bg-slate-800/40 rounded-lg p-1.5 border border-white/5">
                                    <span className="text-[10px] font-bold text-slate-400 line-through truncate max-w-[80px]">{cp.playerName}</span>
                                    <span className={clsx("text-[10px] font-bold tabular-nums px-1.5 py-0.5 rounded", net >= 0 ? "text-emerald-400 bg-emerald-500/10" : "text-rose-400 bg-rose-500/10")} dir="ltr">
                                        {formatCurrency(net, net >= 0 ? "+" : "-")}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};
